import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  createCatalogTemplate,
  getCatalogTemplate,
  getCatalogTemplateDocument,
  listCatalogs,
  updateCatalogTemplate,
  updateCatalogTemplateDocument,
  uploadMedia,
} from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { usePageTitle } from '../../../layout/PageTitleContext';
import TemplateStudioEditor from '../editor/TemplateStudioEditor';
import {
  defaultTemplateSpec,
  prepareTemplateSpecForSave,
  serializeTemplateSpec,
} from '../private/templateSpecUtils';
import { resolveCatalogTemplateSlug, slugifyCatalogTemplate } from '../private/catalogTemplateSlug';
import { mediaFileUrl, newClientId } from '../private/catalogCompositionUtils';
import useCatalogTemplateDesigner from '../hooks/useCatalogTemplateDesigner';

const emptyMeta = () => ({
  name: '',
  slug: '',
  description: '',
  preview_media_id: '',
  preview_media_url: '',
  is_default: false,
  is_public: false,
});

const uploadTemplatePreviewBlob = async ({
  blob,
  layoutId,
  layoutName,
  draftScopeId,
  isNew,
}) => {
  const formData = new FormData();
  const safeName = slugifyCatalogTemplate(layoutName) || 'preview';
  formData.append('file', blob, `${safeName}-preview.png`);
  formData.append('metadata_key', 'catalog_layout_preview');
  formData.append(
    'metadata_json',
    JSON.stringify({
      id: isNew ? draftScopeId : layoutId,
      url: isNew ? '/catalogs/templates/new' : `/catalogs/templates/${layoutId}/edit`,
      title: layoutName || safeName,
      order: 0,
    }),
  );
  const res = await uploadMedia(formData);
  const media = res?.data?.media ?? res?.data ?? res;
  const mediaId = media?.id ?? '';
  const filePath = media?.file_path ?? '';
  if (!mediaId) {
    throw new Error('Preview upload failed');
  }
  return { mediaId, filePath };
};

const TemplateEditorPage = () => {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const { setPageTitle } = usePageTitle();
  const isDesigner = useCatalogTemplateDesigner();
  const [meta, setMeta] = useState(emptyMeta);
  const [documentJson, setDocumentJson] = useState(serializeTemplateSpec(defaultTemplateSpec()));
  const [demoCatalogId, setDemoCatalogId] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '' });
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [mainTab, setMainTab] = useState('details');
  const documentRef = useRef(documentJson);
  const editorRef = useRef(null);
  const draftScopeId = useMemo(() => newClientId(), []);

  useEffect(() => {
    setPageTitle(translate(isNew ? 'catalog.templates.create' : 'catalog.templates.edit'));
    return () => setPageTitle(null);
  }, [isNew, setPageTitle, translate]);

  useEffect(() => {
    if (!isDesigner) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await listCatalogs({ page: 0, page_size: 1 });
        const items = res?.data?.catalogs || res?.data?.items || res?.data || [];
        if (!cancelled && Array.isArray(items) && items[0]?.id) {
          setDemoCatalogId(items[0].id);
        }
      } catch {
        /* optional demo catalog */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDesigner]);

  useEffect(() => {
    if (isNew || !id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [templateRes, docRes] = await Promise.all([
          getCatalogTemplate(id),
          getCatalogTemplateDocument(id),
        ]);
        if (cancelled) return;
        const template = templateRes?.data ?? templateRes;
        const doc = docRes?.data ?? docRes;
        setMeta({
          name: template.name || '',
          slug: template.slug || '',
          description: template.description || '',
          preview_media_id: template.preview_media_id || '',
          preview_media_url: template.preview_media_url || '',
          is_default: !!template.is_default,
          is_public: !!template.is_public,
          is_system: !!template.is_system,
        });
        const nextDoc = doc.document_json || template.document_json || serializeTemplateSpec(defaultTemplateSpec());
        setDocumentJson(nextDoc);
        documentRef.current = nextDoc;
        setPreviewRefreshKey((value) => value + 1);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  const handleDocumentChange = useCallback((serialized) => {
    documentRef.current = serialized;
    setDocumentJson(serialized);
    setPreviewRefreshKey((value) => value + 1);
  }, []);

  const handleNameChange = (value) => {
    setMeta((m) => {
      const next = { ...m, name: value };
      if (isNew) {
        next.slug = slugifyCatalogTemplate(value);
      }
      return next;
    });
  };

  const handleSlugChange = (value) => {
    if (!isNew) return;
    setMeta((m) => ({ ...m, slug: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const name = meta.name?.trim() || '';
      if (!name) {
        throw new Error(translate('catalog.templates.field_name'));
      }
      const slug = isNew
        ? resolveCatalogTemplateSlug(name, meta.slug)
        : String(meta.slug || '').trim().toLowerCase();
      if (!slug) {
        throw new Error(translate('catalog.templates.field_slug'));
      }
      if (isNew) {
        setMeta((m) => ({ ...m, slug }));
      }
      if (!demoCatalogId) {
        throw new Error(translate('catalog.templates.preview_requires_catalog'));
      }

      const rawDocJson = editorRef.current?.getDocumentJson?.() ?? documentRef.current;
      const parsed = JSON.parse(rawDocJson);
      const prepared = prepareTemplateSpecForSave(parsed);
      const docJson = serializeTemplateSpec(prepared);
      documentRef.current = docJson;
      setDocumentJson(docJson);

      const previewBlob = await editorRef.current?.captureDesktopPreview?.();
      if (!previewBlob) {
        throw new Error(translate('catalog.templates.preview_capture_failed'));
      }

      const payload = {
        name,
        slug,
        description: meta.description,
        is_default: meta.is_default,
        is_public: meta.is_public,
        preview_media_id: '',
      };

      if (isNew) {
        payload.document_json = docJson;
        const { mediaId: previewMediaId } = await uploadTemplatePreviewBlob({
          blob: previewBlob,
          layoutId: '',
          layoutName: name,
          draftScopeId,
          isNew: true,
        });
        payload.preview_media_id = previewMediaId;
        const res = await createCatalogTemplate(payload);
        const created = res?.data ?? res;
        await updateCatalogTemplateDocument(created.id, { document_json: docJson });
        navigate(`/catalogs/templates/${created.id}/edit`, { replace: true });
      } else {
        const { mediaId: previewMediaId, filePath: previewFilePath } = await uploadTemplatePreviewBlob({
          blob: previewBlob,
          layoutId: id,
          layoutName: name,
          draftScopeId,
          isNew: false,
        });
        payload.preview_media_id = previewMediaId;
        await updateCatalogTemplate(id, payload);
        await updateCatalogTemplateDocument(id, { document_json: docJson });
        setMeta((m) => ({
          ...m,
          slug,
          preview_media_id: previewMediaId,
          preview_media_url: previewFilePath || m.preview_media_url,
        }));
      }

      setPreviewRefreshKey((value) => value + 1);
      setSnack({ open: true, message: translate('common.saved') });
    } catch (err) {
      setError(err.message);
      setSnack({ open: true, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!isDesigner) {
    return (
      <Alert severity="warning">{translate('catalog.templates.designer_only')}</Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="text.secondary">{translate('common.loading')}</Typography>
      </Box>
    );
  }

  const previewImageUrl = mediaFileUrl(meta.preview_media_url);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/catalogs/templates')}>
          {translate('common.back')}
        </Button>
        <Tabs
          value={mainTab}
          onChange={(_, value) => value && setMainTab(value)}
          sx={{ minHeight: 40, flex: { xs: '1 1 100%', sm: '0 1 auto' }, mx: { sm: 'auto' } }}
        >
          <Tab value="details" label={translate('catalog.templates.form_tab_details')} />
          <Tab value="editor" label={translate('catalog.templates.form_tab_editor')} />
        </Tabs>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
          onClick={() => void handleSave()}
          sx={{ ml: { sm: 'auto' } }}
        >
          {translate('common.save')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mainTab === 'details' && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label={translate('catalog.templates.field_name')}
              value={meta.name}
              onChange={(e) => handleNameChange(e.target.value)}
            />
            <TextField
              label={translate('catalog.templates.field_slug')}
              value={meta.slug}
              disabled={!isNew || !!meta.is_system}
              helperText={
                meta.is_system
                  ? translate('catalog.templates.system_hint')
                  : !isNew
                    ? translate('catalog.templates.slug_readonly_hint')
                    : translate('catalog.templates.slug_hint')
              }
              onChange={(e) => handleSlugChange(e.target.value)}
            />
            <TextField
              label={translate('catalog.templates.field_description')}
              value={meta.description}
              onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))}
              multiline
              minRows={3}
              sx={{ gridColumn: { md: '1 / -1' } }}
            />
          </Box>
          {previewImageUrl ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {translate('catalog.templates.field_preview_image')}
              </Typography>
              <Box
                component="img"
                src={previewImageUrl}
                alt={meta.name || translate('catalog.templates.field_preview_image')}
                sx={{
                  width: '100%',
                  maxWidth: 520,
                  maxHeight: 300,
                  objectFit: 'cover',
                  display: 'block',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              />
            </Box>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {translate('catalog.templates.preview_auto_hint')}
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: mainTab === 'editor' ? 'block' : 'none' }} aria-hidden={mainTab !== 'editor'}>
        {demoCatalogId ? (
          <TemplateStudioEditor
            ref={editorRef}
            documentJson={documentJson}
            onDocumentChange={handleDocumentChange}
            catalogId={demoCatalogId}
            refreshKey={previewRefreshKey}
          />
        ) : (
          <Alert severity="info">{translate('catalog.templates.preview_requires_catalog')}</Alert>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnack({ open: false, message: '' })}
        message={snack.message}
      />
    </Box>
  );
};

export default TemplateEditorPage;
